import React, { useState, useMemo } from "react";
import {
  Table,
  Button,
  Tag,
  Select,
  Space,
  Card,
  Typography,
  Row,
  Col,
  Grid,
  Input,
} from "antd";
import { SoundFilled, FilterFilled, SearchOutlined } from "@ant-design/icons";
import { vocabularies, IVocabulary } from "../data/database";
import { useTTS } from "../hooks/useTTS";

const { Option } = Select;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const Vocabulary: React.FC = () => {
  const { playAudio, loading } = useTTS();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const categories = useMemo(() => {
    const allCategories = vocabularies.map((item) => item.category);
    return ["All", ...Array.from(new Set(allCategories))];
  }, []);

  const filteredData = useMemo(() => {
    let data = vocabularies;

    // 1. Lọc theo danh mục
    if (selectedCategory !== "All") {
      data = data.filter((item) => item.category === selectedCategory);
    }

    // 2. Lọc theo từ khóa tìm kiếm
    if (searchText) {
      const lowerText = searchText.toLowerCase();
      data = data.filter((item) =>
        item.vietnamese.toLowerCase().includes(lowerText)
      );
    }

    return data;
  }, [selectedCategory, searchText]);

  const columns = [
    {
      title: "TIẾNG THÁI",
      dataIndex: "thai",
      key: "thai",
      width: isMobile ? 120 : "25%",
      fixed: "left" as const,
      render: (text: string) => (
        <span
          style={{
            fontSize: isMobile ? "1.1rem" : "1.4rem",
            fontWeight: 600,
            color: "#FF6A88",
            fontFamily: "Kanit",
            letterSpacing: "0.5px",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "PHIÊN ÂM",
      dataIndex: "pronunciation",
      key: "pronunciation",
      width: isMobile ? 140 : "20%",
      render: (text: string) => (
        <Tag
          style={{
            background: "#fff0f3",
            color: "#d6336c",
            border: "none",
            borderRadius: 12,
            padding: "4px 12px",
            fontSize: isMobile ? "12px" : "14px",
            fontWeight: 500,
            fontFamily: "Kanit",
            whiteSpace: "normal",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "NGHĨA TIẾNG VIỆT",
      dataIndex: "vietnamese",
      key: "vietnamese",
      width: isMobile ? 180 : "30%",
      render: (text: string) => (
        <span
          style={{
            fontSize: isMobile ? "0.95rem" : "1.1rem",
            color: "#495057",
            fontFamily: "Kanit",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "CHỦ ĐỀ",
      dataIndex: "category",
      key: "category",
      width: isMobile ? 100 : undefined,
      render: (text: string) => (
        <Tag
          color="default"
          style={{
            borderRadius: 20,
            fontFamily: "Kanit",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "NGHE",
      key: "action",
      width: 80,
      align: "center" as const,
      fixed: isMobile ? ("right" as const) : undefined,
      render: (_: any, record: IVocabulary) => (
        <Button
          type="primary"
          shape="circle"
          icon={<SoundFilled />}
          loading={loading}
          size={isMobile ? "middle" : "large"}
          onClick={() => playAudio(record.thai)}
          style={{
            background: "linear-gradient(135deg, #FF9A8B 0%, #FF6A88 100%)",
            border: "none",
            boxShadow: "0 4px 12px rgba(255, 106, 136, 0.4)",
          }}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Filter Card */}
      <Card
        bordered={false}
        style={{
          marginBottom: 24,
          background: "#fffbf0",
          borderRadius: 20,
        }}
        bodyStyle={{ padding: isMobile ? "16px" : "20px 32px" }}
      >
        <Row justify="space-between" align="middle" gutter={[0, 16]}>
          {/* Đã xóa cột tiêu đề (md=8) ở đây */}

          <Col span={24}>
            {" "}
            {/* Mở rộng cột điều khiển ra toàn bộ chiều ngang */}
            <Space
              direction={isMobile ? "vertical" : "horizontal"}
              style={{
                width: "100%",
                // Đổi thành space-between để Thanh tìm kiếm nằm trái, Bộ lọc nằm phải (trên Desktop)
                justifyContent: isMobile ? "flex-start" : "space-between",
              }}
            >
              {/* Thanh tìm kiếm */}
              <Input
                placeholder="Tìm nghĩa tiếng Việt..."
                prefix={<SearchOutlined style={{ color: "#FF6A88" }} />}
                allowClear
                size={isMobile ? "middle" : "large"}
                style={{
                  width: isMobile ? "100%" : 350, // Tăng kích thước thanh tìm kiếm lên chút cho đẹp
                  fontFamily: "Kanit",
                  borderRadius: 16,
                }}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />

              <Select
                defaultValue="All"
                size={isMobile ? "middle" : "large"}
                suffixIcon={<FilterFilled style={{ color: "#FF6A88" }} />}
                style={{
                  width: "100%",
                  minWidth: isMobile ? "100%" : 220,
                  fontFamily: "Kanit",
                }}
                onChange={(value) => {
                  setSelectedCategory(value);
                  setCurrentPage(1);
                }}
              >
                <Option value="All">🌈 Tất cả chủ đề</Option>
                {categories
                  .filter((c) => c !== "All")
                  .map((c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  ))}
              </Select>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        dataSource={filteredData}
        columns={columns as any}
        rowKey="id"
        scroll={{ x: 700 }}
        pagination={{
          current: currentPage,
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: !isMobile,
          simple: isMobile,
          locale: { items_per_page: "/ trang" },
          showTotal: (total, range) =>
            isMobile ? (
              `${range[0]}-${range[1]}/${total}`
            ) : (
              <Text type="secondary">
                Hiển thị {range[0]}-{range[1]} của <strong>{total}</strong> từ
              </Text>
            ),
          pageSizeOptions: ["5", "10", "20", "50", "100"],
          defaultPageSize: 5,
          position: ["bottomCenter"],
        }}
      />
    </div>
  );
};

export default Vocabulary;
